/**
 * @file Starts Auth0 redirect login or sends an authenticated user to the dashboard.
 */
import { useAuth0 } from "@auth0/auth0-react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getSsoApiAudience, SSO_AUTH0_SCOPE } from "@/auth/sso-env";
import { isAuth0CallbackInProgress } from "@/lib/safe-navigation";

export function LoginRedirect() {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const loginStartedRef = useRef(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (isAuth0CallbackInProgress()) {
      return;
    }
    if (loginStartedRef.current) {
      return;
    }
    loginStartedRef.current = true;
    const audience = getSsoApiAudience();
    void loginWithRedirect({
      appState: { returnTo: "/dashboard" },
      authorizationParams: audience
        ? {
            audience,
            scope: SSO_AUTH0_SCOPE,
          }
        : undefined,
    });
  }, [isLoading, isAuthenticated, loginWithRedirect, navigate]);

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Redirecting to sign in…
      </Typography>
    </Box>
  );
}
